import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { parkingSpots } from './parkingData';
import { useAuth } from './useAuth';
import api from './api';

const ParkingSpotsTable = ({ theme }) => {
  const [spotAssignments, setSpotAssignments] = useState({});
  const [aircraftStatus, setAircraftStatus] = useState({}); // Map of buno -> status
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const { isStaff, groups } = useAuth();

  // Status color mapping (matching AircraftStatusBoard)
  const statusColors = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bbff',
    AWP: '#335d80ff',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d'
  };

  const statusOptions = ['UP', 'XC', 'DN', 'DS', 'PM', 'FCF', 'AWP', 'OTH', 'DET', 'AIMD', 'ACI', 'REI'];

  // Get available sites from parking spots
  const availableSites = useMemo(() => {
    const sites = [...new Set(parkingSpots.map(spot => spot.site).filter(Boolean))];
    return sites.sort();
  }, []);

  // Filter sites based on user groups
  const filteredSites = useMemo(() => {
    if (isStaff) {
      return availableSites;
    }
    // Filter to only show sites that match user's groups
    return availableSites.filter(site => groups.includes(site));
  }, [availableSites, groups, isStaff]);

  // Auto-select site if user has exactly one site group
  useEffect(() => {
    if (!isStaff && groups.length > 0 && !selectedSite) {
      const siteGroups = groups.filter(group => availableSites.includes(group));
      if (siteGroups.length === 1) {
        setSelectedSite(siteGroups[0]);
      }
    }
  }, [groups, isStaff, availableSites, selectedSite]);

  // Fetch aircraft status data
  const fetchAircraftStatus = useCallback(async () => {
    try {
      const response = await api.get('/aircraft-status/');
      let aircraftData = response.data;
      
      // Handle paginated response
      if (response.data.results) {
        aircraftData = response.data.results;
      }
      
      // Convert to map: { buno: status }
      const statusMap = {};
      if (Array.isArray(aircraftData)) {
        aircraftData.forEach(aircraft => {
          if (aircraft.buno) {
            statusMap[aircraft.buno] = aircraft.status || '';
          }
        });
      }
      
      setAircraftStatus(statusMap);
    } catch (err) {
      console.error('Error fetching aircraft status:', err);
    }
  }, []);

  // Fetch spot assignments from backend
  const fetchSpotAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const [assignmentsRes] = await Promise.all([
        api.get('/aircraft-spot-assignments/?is_active=true')
      ]);
      const assignments = assignmentsRes.data;

      // The external app can assign the same aircraft to multiple spots when it
      // is merely NEAR a spot (not actually parked), producing duplicate entries
      // for one aircraft. Keep only the most recent assignment per aircraft so
      // each plane appears once, at the spot with the greatest timestamp.
      const latestByAircraft = {};
      assignments.forEach(assignment => {
        // Identify the aircraft by BUNO, falling back to hex_id when BUNO is missing
        const aircraftId = assignment.buno || assignment.hex_id;
        if (!aircraftId) return;

        const existing = latestByAircraft[aircraftId];
        const currentTime = assignment.last_seen_at ? new Date(assignment.last_seen_at).getTime() : 0;
        const existingTime = existing && existing.last_seen_at ? new Date(existing.last_seen_at).getTime() : -1;

        if (!existing || currentTime > existingTime) {
          latestByAircraft[aircraftId] = assignment;
        }
      });

      // Convert to map: { site_spot: { buno, hex_id, callsign, ... } }
      // Use site_spot as key to handle duplicate spot names across sites
      const assignmentsMap = {};
      Object.values(latestByAircraft).forEach(assignment => {
        const key = `${assignment.site || ''}_${assignment.parking_spot}`;
        assignmentsMap[key] = {
          buno: assignment.buno || null,
          hex_id: assignment.hex_id,
          callsign: assignment.callsign || '',
          timestamp: assignment.last_seen_at,
          site: assignment.site
        };
      });
      
      setSpotAssignments(assignmentsMap);
    } catch (err) {
      console.error('Error fetching spot assignments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount and set up polling
  useEffect(() => {
    fetchAircraftStatus();
    fetchSpotAssignments();
    const interval = setInterval(() => {
      fetchAircraftStatus();
      fetchSpotAssignments();
    }, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, [fetchAircraftStatus, fetchSpotAssignments]);

  // Filter spots by selected site
  const filteredSpots = useMemo(() => {
    let spots = parkingSpots;
    
    // Filter by site if selected
    if (selectedSite) {
      spots = spots.filter(spot => spot.site === selectedSite);
    } else if (!isStaff) {
      // If no site selected and user is not staff, show only their accessible sites
      spots = spots.filter(spot => filteredSites.includes(spot.site));
    }
    
    // Sort by site first, then by spot name
    return spots.sort((a, b) => {
      // First sort by site
      const aSite = a.site || '';
      const bSite = b.site || '';
      if (aSite !== bSite) {
        return aSite.localeCompare(bSite);
      }
      
      // Then sort by spot name (extract row letter and number for proper sorting)
      const aMatch = a.spot.match(/^([A-Z]+)(\d+)$/);
      const bMatch = b.spot.match(/^([A-Z]+)(\d+)$/);
      
      if (aMatch && bMatch) {
        const aRow = aMatch[1];
        const bRow = bMatch[1];
        const aNum = parseInt(aMatch[2]);
        const bNum = parseInt(bMatch[2]);
        
        if (aRow !== bRow) {
          return aRow.localeCompare(bRow);
        }
        return aNum - bNum;
      }
      return a.spot.localeCompare(b.spot);
    });
  }, [selectedSite, isStaff, filteredSites]);

  // Create merged spots with assignments and status
  const mergedSpots = useMemo(() => {
    return filteredSpots.map(spot => {
      const key = `${spot.site || ''}_${spot.spot}`;
      const assignment = spotAssignments[key];
      const buno = assignment?.buno || null;
      const status = buno ? (aircraftStatus[buno] || '') : '';
      
      return {
        ...spot,
        assignedBuno: buno,
        assignedHexId: assignment?.hex_id || null,
        assignedCallsign: assignment?.callsign || null,
        assignedTimestamp: assignment?.timestamp || null,
        assignedStatus: status
      };
    }).filter(spot => {
      // Filter by status if selected
      if (selectedStatus) {
        if (selectedStatus === 'unassigned') {
          return !spot.assignedBuno;
        }
        return spot.assignedStatus === selectedStatus;
      }
      return true;
    });
  }, [filteredSpots, spotAssignments, aircraftStatus, selectedStatus]);

  return (
    <div style={{
      padding: '20px',
      height: '100%',
      overflow: 'auto',
      backgroundColor: theme === 'dark' ? '#121212' : '#f8f9fa'
    }}>
      {/* Filters */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Filter by Site:
          </label>
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#2d3748' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            <option value="">All Sites</option>
            {filteredSites.map(site => (
              <option key={site} value={site}>{site}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Filter by Status:
          </label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#2d3748' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
            <option value="unassigned">Unassigned</option>
          </select>
        </div>
        {loading && (
          <span style={{ color: theme === 'dark' ? '#a0aec0' : '#718096', fontSize: '14px' }}>
            Loading...
          </span>
        )}
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: theme === 'dark' ? '#1a1a1a' : 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{
              backgroundColor: theme === 'dark' ? '#2d3748' : '#f7fafc',
              borderBottom: `2px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`
            }}>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 'bold',
                fontSize: '14px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                borderRight: `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`
              }}>
                Site
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 'bold',
                fontSize: '14px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                borderRight: `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`
              }}>
                Spot
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 'bold',
                fontSize: '14px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                borderRight: `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`
              }}>
                Assigned BUNO
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 'bold',
                fontSize: '14px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {mergedSpots.length === 0 ? (
              <tr>
                <td colSpan="4" style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: theme === 'dark' ? '#a0aec0' : '#718096',
                  fontSize: '14px'
                }}>
                  No parking spots found
                </td>
              </tr>
            ) : (
              mergedSpots.map((spot, index) => (
                <tr
                  key={`${spot.site}_${spot.spot}`}
                  style={{
                    backgroundColor: index % 2 === 0
                      ? (theme === 'dark' ? '#1a1a1a' : 'white')
                      : (theme === 'dark' ? '#2d3748' : '#f7fafc'),
                    borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`,
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4a5568' : '#edf2f7';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0
                      ? (theme === 'dark' ? '#1a1a1a' : 'white')
                      : (theme === 'dark' ? '#2d3748' : '#f7fafc');
                  }}
                >
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                    fontWeight: '500',
                    borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                  }}>
                    {spot.site || '-'}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                    fontWeight: '500',
                    borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                  }}>
                    {spot.spot}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: spot.assignedBuno
                      ? (theme === 'dark' ? '#4ade80' : '#28a745')
                      : (theme === 'dark' ? '#a0aec0' : '#718096'),
                    fontWeight: spot.assignedBuno ? '500' : 'normal',
                    borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                  }}>
                    {spot.assignedBuno || '-'}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '14px'
                  }}>
                    {spot.assignedStatus ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: statusColors[spot.assignedStatus] || '#6c757d',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }}>
                        {spot.assignedStatus}
                      </span>
                    ) : (
                      <span style={{
                        color: theme === 'dark' ? '#a0aec0' : '#718096'
                      }}>
                        -
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{
        marginTop: '20px',
        padding: '12px 16px',
        backgroundColor: theme === 'dark' ? '#2d3748' : '#f7fafc',
        borderRadius: '6px',
        fontSize: '14px',
        color: theme === 'dark' ? '#a0aec0' : '#718096'
      }}>
        Showing {mergedSpots.length} parking spot{mergedSpots.length !== 1 ? 's' : ''}
        {selectedSite && ` at ${selectedSite}`}
        {mergedSpots.length > 0 && (
          <span style={{ marginLeft: '16px' }}>
            ({mergedSpots.filter(s => s.assignedBuno).length} assigned)
          </span>
        )}
      </div>
    </div>
  );
};

export default ParkingSpotsTable;

